import { Controller } from "@hotwired/stimulus";
import {
  TOOLBAR_ITEMS,
  HEADING_LEVELS,
  applyHeadingItem,
  loadTiptapModules,
  buildTiptapExtensions,
  createTiptapEditor,
  execTiptapCommand,
  isTiptapItemActive,
  handleLinkPrompt,
  handleImagePrompt,
} from "../tiptap/shared";

// TipTap rich text editor para el sidebar form del admin (contenedor tipo
// textarea — bordes, fondo, toolbar inline al tope).
//
// La logica compartida con `showcase--inline-tiptap-editor` vive en
// `controllers/tiptap/shared.js`: catalogo de items, comandos, active checks,
// carga lazy, creacion del editor. Este controller solo se ocupa del DOM/CSS
// del contenedor inline (clases `tiptap-toolbar`, `tiptap-toolbar-btn`, etc.
// con estilos en `manage/tiptap_editor.css`) y de los handlers admin-only
// (upload de imagenes, embeds, button template).

export default class extends Controller {
  static targets = ["input", "editor", "toolbar"];
  static values = {
    items: { type: Array, default: ["bold", "italic", "underline"] },
    placeholder: { type: String, default: "Escribe algo..." },
    minHeight: { type: Number, default: 100 },
    maxHeight: { type: Number, default: 400 },
    uploadUrl: { type: String, default: "/image/create" },
    floating: { type: Boolean, default: false },
  };

  async connect() {
    this._destroyed = false;

    const modules = await loadTiptapModules({ withMediaExtensions: true });
    if (this._destroyed) return;

    const extensions = buildTiptapExtensions({
      modules,
      items: this.itemsValue,
      placeholder: this.placeholderValue,
    });

    this.editor = createTiptapEditor({
      modules,
      element: this.editorTarget,
      content: this.inputTarget.value || "",
      extensions,
      editorProps: { attributes: { class: "prose" } },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML();
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
      },
    });

    this.editorTarget.style.minHeight = `${this.minHeightValue}px`;
    this.editorTarget.style.maxHeight = `${this.maxHeightValue}px`;

    this._buildToolbar();
  }

  disconnect() {
    this._destroyed = true;
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  // --- Toolbar rendering ---

  _buildToolbar() {
    if (!this.hasToolbarTarget) return;

    const items = this.itemsValue;
    const toolbar = this.toolbarTarget;
    toolbar.innerHTML = "";

    const headingItems = items.filter((i) => HEADING_LEVELS.includes(i));

    items.forEach((item) => {
      // Skip individual heading items — they're grouped en un dropdown
      if (HEADING_LEVELS.includes(item)) {
        if (item === headingItems[0])
          this._addHeadingDropdown(toolbar, headingItems);
        return;
      }

      if (item === "color") {
        this._addColorPickers(toolbar);
        return;
      }

      const def = TOOLBAR_ITEMS[item];
      if (!def) return;

      const btnWrap = this._createToolbarBtn(
        def.icon,
        () => this._execItem(item),
        def.tooltip,
      );
      btnWrap.dataset.tiptapItem = item;
      toolbar.appendChild(btnWrap);
    });

    this.editor.on("selectionUpdate", () => this._updateActiveStates());
    this.editor.on("transaction", () => this._updateActiveStates());
  }

  _createToolbarBtn(iconClass, onClick, tooltip) {
    const wrapper = document.createElement("span");
    wrapper.className = "tiptap-tooltip-wrap";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tiptap-toolbar-btn";
    btn.innerHTML = `<i class="${iconClass}"></i>`;
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    });
    wrapper.appendChild(btn);

    if (tooltip) {
      const tip = document.createElement("span");
      tip.className = "tiptap-tooltip";
      tip.textContent = tooltip;
      wrapper.appendChild(tip);
    }

    return wrapper;
  }

  _addHeadingDropdown(toolbar, levels) {
    const wrapper = document.createElement("div");
    wrapper.className = "tiptap-heading-dropdown relative";

    const triggerWrap = this._createToolbarBtn(
      "fa-solid fa-heading",
      () => {
        menu.classList.toggle("hidden");
      },
      "Encabezado",
    );
    triggerWrap.dataset.tiptapItem = "heading";
    wrapper.appendChild(triggerWrap);

    const menu = document.createElement("div");
    menu.className =
      "hidden absolute top-full left-0 z-10 bg-white rounded-lg shadow-sm border border-[#eaeaea] py-1 min-w-[140px]";

    // Renderiza SOLO los heading levels que el caller pidio en `items:`.
    // Antes iteraba `HEADING_LEVEL_NUMBERS` hardcoded — incumplia la paridad
    // con el inline floating (que respeta los items recibidos).
    levels.forEach((level) => {
      const num = parseInt(level.replace("h", ""), 10);
      if (!num || num < 1 || num > 6) return;
      const item = document.createElement("button");
      item.type = "button";
      item.className =
        "flex items-center gap-2 w-full px-3 py-1.5 text-[13px] hover:bg-[#f4f4f4] text-left";
      item.innerHTML = `<i class="fa-solid fa-h${num} w-4 text-center"></i> <span>Encabezado ${num}</span>`;
      item.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        applyHeadingItem(this.editor, { level: num });
        menu.classList.add("hidden");
      });
      menu.appendChild(item);
    });

    wrapper.appendChild(menu);
    toolbar.appendChild(wrapper);

    document.addEventListener("click", (e) => {
      if (!wrapper.contains(e.target)) menu.classList.add("hidden");
    });
  }

  _addColorPickers(toolbar) {
    toolbar.appendChild(
      this._createColorPicker(
        "fa-regular fa-text",
        "Color de texto",
        "#000000",
        "textColor",
        () => this.editor.getAttributes("textStyle").color || "#000000",
      ),
    );

    toolbar.appendChild(
      this._createColorPicker(
        "fa-regular fa-fill-drip",
        "Color de relleno",
        "#feec17",
        "highlight",
        () => this.editor.getAttributes("highlight").color || "transparent",
      ),
    );
  }

  _createColorPicker(
    iconClass,
    tooltip,
    defaultColor,
    colorType,
    getCurrentColor,
  ) {
    const wrapper = document.createElement("span");
    wrapper.className = "tiptap-tooltip-wrap";

    const container = document.createElement("div");
    container.className = "tiptap-color-input";

    const input = document.createElement("input");
    input.type = "color";
    input.value = defaultColor;
    input.addEventListener("mousedown", (e) => e.preventDefault());
    input.addEventListener("click", (e) => {
      e.preventDefault();
      const detached = document.createElement("input");
      detached.type = "color";
      detached.value = input.value;
      const rect = container.getBoundingClientRect();
      detached.style.position = "fixed";
      detached.style.left = `${rect.left}px`;
      detached.style.top = `${rect.bottom}px`;
      detached.style.width = "0";
      detached.style.height = "0";
      detached.style.opacity = "0";
      detached.style.pointerEvents = "none";
      document.body.appendChild(detached);
      detached.addEventListener("input", (ev) => {
        const color = ev.target.value;
        if (colorType === "textColor") {
          this.editor.chain().focus().setColor(color).run();
        } else {
          this.editor.chain().focus().setHighlight({ color }).run();
        }
        bar.style.backgroundColor = color;
        input.value = color;
      });
      detached.addEventListener("change", () => {
        detached.remove();
      });
      detached.click();
    });

    const display = document.createElement("span");
    const icon = document.createElement("em");
    icon.innerHTML = `<i class="${iconClass}"></i>`;
    const bar = document.createElement("i");
    bar.style.backgroundColor = defaultColor;
    display.appendChild(icon);
    display.appendChild(bar);

    container.appendChild(input);
    container.appendChild(display);
    wrapper.appendChild(container);

    this.editor.on("selectionUpdate", () => {
      const color = getCurrentColor();
      bar.style.backgroundColor = color;
      input.value = color === "transparent" ? defaultColor : color;
    });

    if (tooltip) {
      const tip = document.createElement("span");
      tip.className = "tiptap-tooltip";
      tip.textContent = tooltip;
      wrapper.appendChild(tip);
    }

    return wrapper;
  }

  _updateActiveStates() {
    if (!this.hasToolbarTarget) return;

    this.toolbarTarget.querySelectorAll("[data-tiptap-item]").forEach((el) => {
      const item = el.dataset.tiptapItem;
      const btn = el.classList.contains("tiptap-toolbar-btn")
        ? el
        : el.querySelector(".tiptap-toolbar-btn");
      if (!btn) return;

      const isActive = isTiptapItemActive(this.editor, item);
      btn.classList.toggle("tiptap-toolbar-btn--active", isActive);
    });
  }

  // --- Command dispatch ---

  // Delegado al exec compartido. Los handlers custom (`_handleXxx`) son
  // metodos de este controller — se exponen como customHandlers para que
  // shared.execTiptapCommand los invoque.
  _execItem(item) {
    execTiptapCommand({
      editor: this.editor,
      item,
      customHandlers: {
        _handleLink: (editor) => handleLinkPrompt(editor),
        _handleImage: () => this._handleImage(),
        _handleVideoEmbed: () => this._handleVideoEmbed(),
        _handleButtonTemplate: () => this._handleButtonTemplate(),
        _handleEmbed: () => this._handleEmbed(),
      },
    });
  }

  // --- Handlers admin (image, video, button, embed) ---

  _handleImage() {
    handleImagePrompt(this.editor, this.uploadUrlValue, (show) =>
      this._showLoading(show),
    );
  }

  _showLoading(show) {
    const existing = this.element.querySelector(".tiptap-loading-overlay");
    if (show && !existing) {
      const overlay = document.createElement("div");
      overlay.className = "tiptap-loading-overlay";
      overlay.innerHTML =
        '<div class="tiptap-loading-spinner"><i class="fa-solid fa-spinner fa-spin"></i> Subiendo imagen...</div>';
      this.editorTarget.parentElement.appendChild(overlay);
    } else if (!show && existing) {
      existing.remove();
    }
  }

  _handleVideoEmbed() {
    const videoUrl = prompt("Ingresa una URL de Vimeo.com o Youtube.com");
    if (!videoUrl) return;

    try {
      const url = new URL(videoUrl);
      const hostname = url.hostname.replace("www.", "");

      if (hostname === "youtube.com" || hostname === "youtu.be") {
        let videoId;
        if (hostname === "youtu.be") {
          videoId = url.pathname.slice(1);
        } else {
          videoId = url.searchParams.get("v");
        }
        if (videoId) {
          this.editor
            .chain()
            .focus()
            .setYouTubeVideo({
              src: `https://www.youtube.com/embed/${videoId}`,
            })
            .run();
        }
      } else if (hostname === "vimeo.com") {
        const videoId = url.pathname.split("/").pop();
        if (videoId) {
          this.editor
            .chain()
            .focus()
            .setVimeoVideo({ src: `https://player.vimeo.com/video/${videoId}` })
            .run();
        }
      } else {
        alert("URL no valida. Usa una URL de YouTube o Vimeo.");
      }
    } catch {
      alert("URL no valida");
    }
  }

  _handleButtonTemplate() {
    let label, href, size;

    if (this.editor.isActive("buttonTemplate")) {
      const attrs = this.editor.getAttributes("buttonTemplate");
      label = prompt("Texto del boton", attrs.label);
      if (label === null) return;
      href = prompt("URL del boton", attrs.href);
      if (href === null) return;
      size = prompt("Tamano (small, medium, large)", attrs.size);
      if (size === null) return;

      this.editor
        .chain()
        .focus()
        .updateAttributes("buttonTemplate", { label, href, size })
        .run();
    } else {
      label = prompt("Texto del boton", "Ir a Flycrew");
      if (label === null) return;
      href = prompt("URL del boton", "https://flycrew.com");
      if (href === null) return;
      size = prompt("Tamano (small, medium, large)", "small");
      if (size === null) return;

      this.editor
        .chain()
        .focus()
        .insertContent({
          type: "buttonTemplate",
          attrs: { label, href, size: size || "small" },
        })
        .run();
    }
  }

  _handleEmbed() {
    let embedCode;
    if (this.editor.isActive("embed")) {
      const attrs = this.editor.getAttributes("embed");
      embedCode = prompt(
        "Codigo de incrustacion (iframe)",
        `<iframe src="${attrs.src}"></iframe>`,
      );
    } else {
      embedCode = prompt(
        "Pega el codigo de incrustacion (iframe)",
        '<iframe src="..."></iframe>',
      );
    }

    if (!embedCode) return;

    if (!embedCode.includes("<iframe")) {
      alert("Debes pegar un iframe valido.");
      return;
    }

    const match = embedCode.match(/src="([^"]+)"/);
    const src = match?.[1];

    if (!src) {
      alert("No se pudo extraer el atributo src del iframe.");
      return;
    }

    if (this.editor.isActive("embed")) {
      this.editor.chain().focus().updateAttributes("embed", { src }).run();
    } else {
      this.editor
        .chain()
        .focus()
        .insertContent({ type: "embed", attrs: { src } })
        .run();
    }
  }
}
